import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Sparkles, Globe, AlertCircle } from "lucide-react";
import LandingSection from "@/components/landing/LandingSection";
import useTabMusic from "@/hooks/useTabMusic";
import NyxMascot from "@/components/game/NyxMascot";
import { useCurrentLang, setStoredLang } from "@/utils/i18n";

const API = process.env.REACT_APP_BACKEND_URL;

const FOOTER_COPY = {
  it: {
    tagline: "Un GDR narrativo scritto dall'intelligenza artificiale. Mobile + Desktop.",
    game: "Gioco", legal: "Legale",
    login: "Accedi", hall: "Hall of Stories", about: "Chi siamo",
    privacy: "Privacy", terms: "Termini", cookies: "Cookie", contact: "Contatti",
    copy: "© 2026 Arcane Chronicles. Tutti i diritti riservati.",
    stamp: "· Scritto in tempo reale dall'IA ·",
    siteTagline: "AI Narrative RPG",
    navLogin: "Accedi",
    offlineNotice: "Modalità Offline / Contenuti predefiniti attivi",
  },
  en: {
    tagline: "An AI-narrated RPG. Mobile + Desktop.",
    game: "Game", legal: "Legal",
    login: "Sign in", hall: "Hall of Stories", about: "About",
    privacy: "Privacy", terms: "Terms", cookies: "Cookies", contact: "Contact",
    copy: "© 2026 Arcane Chronicles. All rights reserved.",
    stamp: "· Written in real time by AI ·",
    siteTagline: "AI Narrative RPG",
    navLogin: "Sign in",
    offlineNotice: "Offline Mode / Fallback content active",
  },
};

export const FALLBACK_SECTIONS = [
  {
    id: "fallback_hero",
    type: "hero",
    visible: true,
    content: {
      it: {
        eyebrow: "GDR NARRATIVO REATTIVO · POWERED BY AI",
        title: "Ogni parola forgerà\nla tua leggenda",
        subtitle: "Vivi un'avventura fantasy infinita generata in tempo reale.\nCombatti con carte mistiche, influenza il destino del mondo e scolpisci la tua saga.",
        cta_primary_label: "Inizia Gratuitamente",
        cta_primary_href: "/auth",
        cta_secondary_label: "Esplora la Hall of Stories",
        cta_secondary_href: "/hall",
      },
      en: {
        eyebrow: "REACTIVE NARRATIVE RPG · POWERED BY AI",
        title: "Every word will forge\nyour legend",
        subtitle: "Experience an endless fantasy RPG generated in real time.\nBattle with mystical cards, shape world destiny and immortalize your saga.",
        cta_primary_label: "Play for Free",
        cta_primary_href: "/auth",
        cta_secondary_label: "Explore Hall of Stories",
        cta_secondary_href: "/hall",
      },
    },
  },
  {
    id: "fallback_features",
    type: "feature_grid",
    visible: true,
    content: {
      it: {
        eyebrow: "PERCHÉ ARCANE CHRONICLES",
        title: "Un mondo vivo che reagisce davvero alle tue decisioni",
        body: "Dimentica gli schemi prefissati: l'Intelligenza Artificiale narra, illustra e adatta ogni singolo capitolo alle tue scelte e al tuo grimorio.",
        cards: [
          {
            icon: "book",
            title: "Narrazione Viva & Illimitata",
            body: "Nessun albero fisso. Ogni bivio morale, incontro e trama si adatta in tempo reale alle tue parole con prosa d'autore."
          },
          {
            icon: "swords",
            title: "Combattimento Tattico a Carte",
            body: "Colleziona incantesimi, evoca alleati e concatena combo devastanti. Potenzia le carte nell'officina o trova artefatti perduti."
          },
          {
            icon: "trophy",
            title: "Mondo Persistente & Condiviso",
            body: "Condividi le tue saghe memorabili nella Hall of Stories, scambia carte con altri avventurieri e installa la PWA per giocare ovunque."
          }
        ]
      },
      en: {
        eyebrow: "WHY ARCANE CHRONICLES",
        title: "A living realm reacting directly to your choices",
        body: "Forget pre-baked story trees: AI crafts, illustrates, and balances every single chapter around your decisions, ethics, and deck.",
        cards: [
          {
            icon: "book",
            title: "Living & Boundless Story",
            body: "No rigid trees. Every moral crossroad, NPC encounter, and plot twist adapts in real time to your input with evocative prose."
          },
          {
            icon: "swords",
            title: "Tactical Card Battles",
            body: "Cast spells, summon allies, and execute deadly combos. Upgrade cards in the workshop or unearth mythical relics."
          },
          {
            icon: "trophy",
            title: "Shared & Persistent Realm",
            body: "Showcase legendary runs in the Hall of Stories, trade cards with travelers, and install the PWA for seamless cross-platform play."
          }
        ]
      }
    }
  },
  {
    id: "fallback_how_to_play",
    type: "how_to_play",
    visible: true,
    content: {
      it: {
        eyebrow: "IL CICLO DI GIOCO",
        title: "Come si gioca ad Arcane Chronicles",
        body: "Un'esperienza progettata per immergerti all'istante: rapida da apprendere, infinita da esplorare.",
        steps: [
          { icon: "crown", title: "Crea il Personaggio", body: "Scegli classe, nome e ambientazione. Ricevi equipaggiamento e carte iniziali." },
          { icon: "book", title: "Vivi il Capitolo", body: "Leggi la scena illustrata dall'IA. Scegli un'opzione o digita la tua azione liberamente." },
          { icon: "swords", title: "Affronta lo Scontro", body: "Gestisci Punti Azione, Mana e sinergie per sconfiggere creature e boss epici." },
          { icon: "sparkles", title: "Ottieni & Potenzia", body: "Guadagna carte rare, oro ed essenza magica. Potenzia il grimorio nell'officina." },
          { icon: "flag", title: "Cambia la Storia", body: "Le tue scelte plasmano il destino del regno e forgiano una cronaca da tramandare." }
        ]
      },
      en: {
        eyebrow: "THE CORE LOOP",
        title: "How Arcane Chronicles Plays",
        body: "Engineered for immediate immersion: simple to learn, limitless to master.",
        steps: [
          { icon: "crown", title: "Create Your Hero", body: "Pick class, name, and world setting. Receive your starting gear and initial grimoire." },
          { icon: "book", title: "Live the Chapter", body: "Immerse yourself in AI-illustrated prose. Pick a choice or type your custom action." },
          { icon: "swords", title: "Tactical Combat", body: "Manage Action Points, Mana, and synergy cards to overcome treacherous foes and bosses." },
          { icon: "sparkles", title: "Loot & Upgrade", body: "Earn rare cards, gold, and dust. Upgrade and enchant cards in your mystical workshop." },
          { icon: "flag", title: "Shape the Chronicle", body: "Your decisions alter the world permanently and carve a saga for all to admire." }
        ]
      }
    }
  },
  {
    id: "fallback_preview",
    type: "split",
    visible: true,
    content: {
      it: {
        eyebrow: "ESTRATTO NARRATIVO",
        title: "Dalla penna dell'Oracolo al tuo grimorio",
        subtitle: "Ogni bivio apre sentieri inesplorati",
        body: "«L'aria si fa gelida tra i monoliti della Cripta delle Maree. Due sentinelle di pietra sbarrano l'arco d'ingresso, mentre una voce antica sussurra il tuo nome dal fondo dell'abisso.» Scegli se sguainare la lama, intonare una formula arcana o negoziare il passaggio.",
        stats: [
          { value: "100%", label: "Prosa generata in tempo reale" },
          { value: "5 Classi", label: "Guerriero, Mago, Ladro, Ranger, Necromante" }
        ]
      },
      en: {
        eyebrow: "NARRATIVE PREVIEW",
        title: "From the Oracle's quill to your grimoire",
        subtitle: "Every crossroads opens uncharted paths",
        body: "«The air turns bitterly cold between the coral monoliths of the Sunken Crypt. Two stone sentinels bar the entrance while an ancient voice whispers your name from the deep abyss.» Will you draw your blade, chant a secret cantrip, or negotiate passage?",
        stats: [
          { value: "100%", label: "Real-time AI prose" },
          { value: "5 Classes", label: "Warrior, Mage, Rogue, Ranger, Necromancer" }
        ]
      }
    }
  },
  {
    id: "fallback_cta",
    type: "cta",
    visible: true,
    content: {
      it: {
        title: "Il tuo capitolo ti attende.\nSei pronto a scriverlo?",
        body: "Inizia subito la tua leggenda in meno di due minuti. Nessuna installazione richiesta: gioca direttamente dal browser o installa l'app per mobile e desktop.",
        button_label: "Inizia l'Avventura Gratuitamente",
        button_href: "/auth"
      },
      en: {
        title: "Your chapter awaits.\nAre you ready to write it?",
        body: "Begin your legend right now in under two minutes. No friction: play right in your browser or install the PWA on mobile and desktop.",
        button_label: "Begin Your Adventure for Free",
        button_href: "/auth"
      }
    }
  }
];

export default function LandingPage() {
  const [lang, setLang] = useCurrentLang();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cmsOffline, setCmsOffline] = useState(false);
  const location = useLocation();
  const isDraftMode = new URLSearchParams(location.search).get("draft") === "1";

  useTabMusic('landing');

  useEffect(() => {
    let alive = true;
    const fetchContent = async () => {
      try {
        if (isDraftMode) {
          const token = localStorage.getItem("token");
          if (token) {
            const r = await axios.get(`${API}/api/admin/landing/draft`, {
              headers: { Authorization: `Bearer ${token}` },
              timeout: 6000,
            });
            if (alive) {
              const draftSecs = r.data?.draft?.sections;
              if (Array.isArray(draftSecs) && draftSecs.length > 0) {
                setSections(draftSecs);
                return;
              }
            }
          }
        }
        const r = await axios.get(`${API}/api/landing/content`, { timeout: 6000 });
        if (alive) {
          const loadedSecs = r.data?.sections;
          if (Array.isArray(loadedSecs) && loadedSecs.length > 0) {
            setSections(loadedSecs);
            setCmsOffline(false);
          } else {
            // DB/CMS is empty or fresh deployment -> use fallback
            setSections(FALLBACK_SECTIONS);
          }
        }
      } catch {
        if (alive) {
          // Graceful fallback: never leave landing blank!
          setSections(FALLBACK_SECTIONS);
          setCmsOffline(true);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchContent();
    return () => { alive = false; };
  }, [isDraftMode]);

  const t = FOOTER_COPY[lang] || FOOTER_COPY.it;
  const switchLang = (l) => {
    setLang(l);
    setStoredLang(l);
  };

  const activeSections = sections.length > 0 ? sections : FALLBACK_SECTIONS;

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-[#f8fafc] overflow-x-hidden">
      <div
        className="pointer-events-none fixed inset-0 z-[1] opacity-[0.035] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* HEADER */}
      <header className="fixed top-0 inset-x-0 z-50 bg-[#0a0a0c]/85 backdrop-blur-md border-b border-[#cfb53b]/15">
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-2">
          <a href="#top" className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-9 h-9 border border-[#cfb53b]/40 flex items-center justify-center bg-gradient-to-br from-[#cfb53b]/20 to-transparent rounded">
              <Sparkles className="text-[#cfb53b]" size={16} />
            </div>
            <div>
              <p className="font-heading text-base md:text-lg tracking-[0.18em] text-[#cfb53b] leading-none">Arcane Chronicles</p>
              <p className="font-body text-[10px] uppercase tracking-[0.3em] text-[#cfb53b]/40 mt-0.5">{t.siteTagline}</p>
            </div>
          </a>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1 text-[#cfb53b]/70" data-testid="landing-lang-switcher">
              <Globe size={12} />
              <button
                onClick={() => switchLang("it")}
                data-testid="landing-lang-it"
                className={`font-heading text-[10px] uppercase tracking-[0.2em] px-1.5 py-1 transition-colors ${lang === "it" ? "text-[#cfb53b] font-bold" : "text-[#cfb53b]/40 hover:text-[#cfb53b]/80"}`}
              >IT</button>
              <span className="text-[#cfb53b]/20">·</span>
              <button
                onClick={() => switchLang("en")}
                data-testid="landing-lang-en"
                className={`font-heading text-[10px] uppercase tracking-[0.2em] px-1.5 py-1 transition-colors ${lang === "en" ? "text-[#cfb53b] font-bold" : "text-[#cfb53b]/40 hover:text-[#cfb53b]/80"}`}
              >EN</button>
            </div>
            <Link
              to="/auth"
              data-testid="landing-login-btn"
              className="font-heading text-xs md:text-sm uppercase tracking-[0.25em] px-5 md:px-7 py-2.5 border border-[#cfb53b] text-[#cfb53b] hover:bg-[#cfb53b] hover:text-[#0a0a0c] transition-all duration-300 rounded-sm font-semibold"
            >
              {t.navLogin}
            </Link>
          </div>
        </div>
      </header>

      {/* Draft mode or CMS offline status banner (for admins / preview) */}
      {isDraftMode && (
        <div className="fixed top-[60px] inset-x-0 z-40 bg-amber-500/95 text-[#0a0a0c] text-center text-xs font-heading uppercase tracking-[0.2em] py-2 backdrop-blur-md">
          · ANTEPRIMA BOZZA — non pubblicata · <Link to="/admin" className="underline ml-2">Torna al pannello</Link>
        </div>
      )}

      {cmsOffline && isDraftMode && (
        <div className="fixed top-[92px] inset-x-0 z-40 bg-rose-950/90 border-b border-rose-600/50 text-rose-200 text-center text-xs py-1.5 px-4 backdrop-blur-md flex items-center justify-center gap-2">
          <AlertCircle size={14} />
          <span>Backend CMS non raggiungibile. È attivo il fallback statico commerciale.</span>
        </div>
      )}

      <div id="top" />

      {/* DYNAMIC OR FALLBACK SECTIONS */}
      {loading ? (
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-10 h-10 border-2 border-[#cfb53b] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        activeSections
          .filter(s => s.visible !== false)
          .map((s, i) => (
            <LandingSection key={s.id || i} section={s} lang={lang} dark={i % 2 === 1} />
          ))
      )}

      {/* FOOTER */}
      <footer className="bg-[#050506] border-t border-[#cfb53b]/15 px-4 py-14 text-[#cbd5e1]/60">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-3">
              <Sparkles className="text-[#cfb53b]" size={16} />
              <p className="font-heading text-base tracking-[0.18em] text-[#cfb53b]">Arcane Chronicles</p>
            </div>
            <p className="font-body text-sm leading-relaxed max-w-sm">{t.tagline}</p>
          </div>
          <div>
            <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#cfb53b]/70 mb-3">{t.game}</p>
            <ul className="space-y-2 font-body text-sm">
              <li><Link to="/auth" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-login">{t.login}</Link></li>
              <li><Link to="/hall" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-hall">{t.hall}</Link></li>
              <li><Link to="/about" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-about">{t.about}</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-heading text-xs uppercase tracking-[0.2em] text-[#cfb53b]/70 mb-3">{t.legal}</p>
            <ul className="space-y-2 font-body text-sm">
              <li><Link to="/privacy" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-privacy">{t.privacy}</Link></li>
              <li><Link to="/terms" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-terms">{t.terms}</Link></li>
              <li><Link to="/cookies" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-cookies">{t.cookies}</Link></li>
              <li><Link to="/contact" className="hover:text-[#cfb53b] transition-colors" data-testid="footer-contact">{t.contact}</Link></li>
            </ul>
          </div>
        </div>
        <div className="max-w-6xl mx-auto pt-8 border-t border-[#cfb53b]/10 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="font-body text-xs text-[#cbd5e1]/40">{t.copy}</p>
          <p className="font-heading text-[10px] uppercase tracking-[0.3em] text-[#cfb53b]/40">{t.stamp}</p>
        </div>
      </footer>
      <NyxMascot forceLang={lang} />
    </div>
  );
}
